
import { isAllowedPartnerOrigin, isEmbedMode, PARTNER_ORIGIN_ALLOWLIST } from "./embedMode";
import { embedAuth, embedAuthWarn } from "./embedAuthLog";
import { getActingPartner } from "./actingPartner";
import { ROUTES } from "../constant/paths";

export type PortalSurfaceName = "chat" | "shop" | "history" | "trips";

const SURFACE_KEY = "_zenvoya_portal_surface";
const ORIGIN_KEY = "_zenvoya_portal_origin";

const VALID_SURFACES: readonly PortalSurfaceName[] = ["chat", "shop", "history", "trips"];

export const PORTAL_MESSAGES = {
  bookingComplete: "zenvoya:booking-complete",
  goToBooking: "zenvoya:go-to-booking",
  navigateSurface: "zenvoya:navigate-surface",
  sessionRecover: "zenvoya:session-recover",
  reloadHost: "zenvoya:reload-host",
} as const;

export const SESSION_RECOVER_REASONS = {
  tokenExpired: "token-expired",
} as const;

export const PORTAL_INBOUND = {
  setSurface: "zenvoya:set-surface",
  // CRM Branding → Theme Studio live preview: `{ type, branding | null }`, where
  // `branding` is the unsaved form in app-backend's `branding/view` shape.
  themePreview: "zenvoya:theme-preview",
} as const;

export function rememberPortalSurface(surface: PortalSurfaceName): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SURFACE_KEY, surface);
  } catch {
  }
}

export function getPortalSurface(): PortalSurfaceName | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(SURFACE_KEY) as PortalSurfaceName | null;
    return value && VALID_SURFACES.includes(value) ? value : null;
  } catch {
    return null;
  }
}

export function rememberPortalOrigin(origin: string): void {
  if (typeof window === "undefined" || !origin) return;
  try {
    window.sessionStorage.setItem(ORIGIN_KEY, origin);
  } catch {
  }
}

export function getPortalOrigin(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(ORIGIN_KEY);
    return value && isAllowedPartnerOrigin(value) ? value : null;
  } catch {
    return null;
  }
}

export function isPortalEmbedFlow(): boolean {
  return isEmbedMode() && getPortalSurface() !== null && getPortalOrigin() !== null;
}

export function isAppEmbedFlow(): boolean {
  return isEmbedMode() && getActingPartner()?.source === "ticket" && !isPortalEmbedFlow();
}

// Inside an embed, the app's own navigation (desktop rail + history sidebar,
// mobile top bar) shows only on the native app's chat surface. Operator-portal
// frames never show it: the console around the frame has its own sidebar.
export function showsEmbedAppNav(pathname: string): boolean {
  return isAppEmbedFlow() && pathname === ROUTES.PortalChat;
}

export function postToPortal(payload: Record<string, unknown>): boolean {
  if (typeof window === "undefined") return false;
  const parent = window.parent;
  if (!parent || parent === window) return false;

  const known = getPortalOrigin();
  const targets = known ? [known] : [...PARTNER_ORIGIN_ALLOWLIST];
  if (targets.length === 0) {
    embedAuthWarn("portal.post.no-target", {
      type: payload?.type,
      hint: "REACT_APP_PORTAL_ORIGINS is empty — nothing may be posted to a parent frame",
    });
    return false;
  }

  const body = JSON.stringify(payload);
  let sent = 0;
  targets.forEach((origin) => {
    try {
      parent.postMessage(body, origin);
      sent += 1;
    } catch {
    }
  });
  if (sent === 0) return false;
  embedAuth("portal.post", { type: payload?.type, targets: targets.length, resolved: !!known });
  return true;
}
